import { Component, ElementRef, inject, Signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  BreakpointService,
  ClassSelector,
  FormService,
  ScrollService,
  TypedTemplateDirective,
  WindowRef,
} from '@boels/helpers';
import { BoelsUserTypeEnum, CheckoutInputLimitEnum } from '@boels/models';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faChevronLeft,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { NgSelectModule } from '@ng-select/ng-select';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { catchError, EMPTY, switchMap, take, tap } from 'rxjs';
import { FormErrorsComponent } from '../../form/form-errors/form-errors.component';
import { TextInputComponent } from '../../form/input/text-input.component';
import { PhoneComponent } from '../../phone/phone.component';
import { CustomerDetailsService, PaymentFormState } from '@boels/shared-state';
import { Router } from '@angular/router';
import {
  NgClass,
  NgIf,
  NgSwitch,
  NgSwitchCase,
  NgSwitchDefault,
  NgTemplateOutlet,
} from '@angular/common';
import { DebtorSelectComponent } from './debtor-select/debtor-select.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonComponent } from '../../button/button.component';

@Component({
  selector: 'boels-checkout-customer-details',
  templateUrl: './checkout-customer-details.component.html',
  styleUrls: ['./checkout-customer-details.component.scss'],
  imports: [
    NgIf,
    NgClass,
    NgTemplateOutlet,
    NgSwitch,
    NgSwitchCase,
    NgSwitchDefault,
    TypedTemplateDirective,
    FontAwesomeModule,
    TranslocoModule,
    FormsModule,
    ReactiveFormsModule,
    FormErrorsComponent,
    TextInputComponent,
    MatFormFieldModule,
    MatInputModule,
    NgSelectModule,
    PhoneComponent,
    DebtorSelectComponent,
    ButtonComponent,
  ],
  providers: [
    FormService,
    CustomerDetailsService,
    ScrollService,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['checkout', 'form', 'user'],
    },
  ],
})
export class CheckoutCustomerDetailsComponent {
  //Dependency injection
  #router = inject(Router);
  #scrollService = inject(ScrollService);
  #elementRef = inject(ElementRef);
  #winRef = inject(WindowRef);
  #custDetailsService = inject(CustomerDetailsService);
  #breakPointService = inject(BreakpointService);

  readonly faCircleInfo = faCircleInfo;
  readonly maxInputLengths = CheckoutInputLimitEnum;
  readonly userType = BoelsUserTypeEnum;

  readonly paymentFormData: Signal<PaymentFormState | undefined> = toSignal(
    this.#custDetailsService.initFormData().pipe(
      switchMap((paymentFormData) => {
        if (paymentFormData.registerdUser())
          return this.#custDetailsService.patchDebtorFormIfAny(paymentFormData);
        return this.#custDetailsService.setBillingFormMetaData(paymentFormData);
      }),
    ),
  );

  readonly $isMobile = toSignal(this.#breakPointService.mdAndBelow$, {
    initialValue: false,
  });

  getControl(form: FormGroup, key: string, nestedKey?: string): FormControl {
    return this.#custDetailsService.getFormItem<FormControl>(
      form,
      key,
      nestedKey,
    );
  }

  getLoggedInAsDebtor(paymentFormData: PaymentFormState): string {
    return this.#custDetailsService.debtorInfoText(
      paymentFormData.debtorFormMetaData?.debtor(),
    );
  }

  onCustomerTypeChange(paymentFormData: PaymentFormState): void {
    this.#custDetailsService.billingCustomerTypeChange(paymentFormData);
  }

  logoutDebtor(): void {
    this.#custDetailsService.convertCartAndLogout();
  }

  submit(paymentFormData: PaymentFormState): void {
    if (!paymentFormData.form().valid) {
      paymentFormData.form().markAllAsTouched();
      this.#scrollService.scrollToElement(
        ClassSelector.INVALID_FORM_INPUT,
        this.#elementRef,
      );
      return;
    }

    this.#winRef.nativeWindow?.scrollTo({ top: 0, behavior: 'smooth' });
    this.#custDetailsService
      .submitForm(paymentFormData)
      .pipe(
        tap(() => this.#router.navigate(['checkout/delivery-mode'])),
        catchError((error) => {
          this.#custDetailsService.displayError(error);
          return EMPTY;
        }),
        take(1),
      )
      .subscribe();
  }

  back(): void {
    this.#router.navigate(['/cart']);
  }

  protected readonly faArrowLeft = faArrowLeft;
  protected readonly faChevronLeft = faChevronLeft;
  protected readonly faArrowRight = faArrowRight;
}
